import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  GraduationCap, Sparkles, MessageCircle, Zap, AlertTriangle, Star, Ruler,
  MousePointerClick, Users, Loader2, Pencil, Trash2, Eye, EyeOff, Check, Quote,
} from 'lucide-react';
import Button from './ui/Button';
import { AnimatedModal } from './AnimatedComponents';

/** Le categorie della memoria, come le restituisce il backend. */
export type CategoriaApprendimento =
  | 'tone_of_voice' | 'hook' | 'errori' | 'preferenze' | 'regole' | 'cta' | 'contesto';

/** Una cosa che l'assistente ha imparato su questo cliente. */
interface Voce {
  id: string;
  categoria: CategoriaApprendimento;
  regola: string;
  esempio: string | null;
  origine: string | null;
  attivo: boolean;
  conferme: number;
  created_at: string;
  updated_at: string;
}

interface Gruppo {
  categoria: CategoriaApprendimento;
  etichetta: string;
  totale: number;
  attivi: number;
  voci: Voce[];
}

interface Memoria {
  totale: number;
  attivi: number;
  ultimoApprendimento: string | null;
  gruppi: Gruppo[];
}

interface Props {
  clientId: string;
  clientName: string;
  getToken: () => string | null;
  /** Cresce a ogni risposta dell'assistente: fa ricontrollare cosa ha imparato. */
  segnaleNuovoScambio: number;
}

/**
 * Aspetto di ogni cassetto della memoria. I colori non sono decorativi: rosso
 * = cose da non rifare più, verde = formule che funzionano, così a colpo
 * d'occhio si capisce se l'assistente ha imparato più divieti o più ricette.
 */
const STILE_CATEGORIA: Record<
  CategoriaApprendimento,
  { icona: React.ElementType; descrizione: string; testo: string; sfondo: string; bordo: string }
> = {
  tone_of_voice: {
    icona: MessageCircle,
    descrizione: 'Come deve comunicare il cliente: registro, persona, ritmo, distanza dal pubblico.',
    testo: 'text-violet-700 dark:text-violet-300',
    sfondo: 'bg-violet-50 dark:bg-violet-500/10',
    bordo: 'border-violet-200 dark:border-violet-500/30',
  },
  hook: {
    icona: Zap,
    descrizione: 'Strutture di gancio approvate, aggiunte o bocciate dal copywriter.',
    testo: 'text-amber-700 dark:text-amber-300',
    sfondo: 'bg-amber-50 dark:bg-amber-500/10',
    bordo: 'border-amber-200 dark:border-amber-500/30',
  },
  errori: {
    icona: AlertTriangle,
    descrizione: 'Feedback negativi già ricevuti in chat: quello che non deve più ripetere.',
    testo: 'text-red-700 dark:text-red-300',
    sfondo: 'bg-red-50 dark:bg-red-500/10',
    bordo: 'border-red-200 dark:border-red-500/30',
  },
  preferenze: {
    icona: Star,
    descrizione: 'Elementi ricorrenti che questo cliente vuole sempre rispettati.',
    testo: 'text-pink-700 dark:text-pink-300',
    sfondo: 'bg-pink-50 dark:bg-pink-500/10',
    bordo: 'border-pink-200 dark:border-pink-500/30',
  },
  regole: {
    icona: Ruler,
    descrizione: 'Indicazioni operative di scrittura apprese dal copywriter.',
    testo: 'text-sky-700 dark:text-sky-300',
    sfondo: 'bg-sky-50 dark:bg-sky-500/10',
    bordo: 'border-sky-200 dark:border-sky-500/30',
  },
  cta: {
    icona: MousePointerClick,
    descrizione: 'Formule e approcci di chiamata all\'azione che funzionano meglio.',
    testo: 'text-emerald-700 dark:text-emerald-300',
    sfondo: 'bg-emerald-50 dark:bg-emerald-500/10',
    bordo: 'border-emerald-200 dark:border-emerald-500/30',
  },
  contesto: {
    icona: Users,
    descrizione: 'Informazioni sul cliente, sui suoi servizi e sul pubblico a cui parla.',
    testo: 'text-indigo-700 dark:text-indigo-300',
    sfondo: 'bg-indigo-50 dark:bg-indigo-500/10',
    bordo: 'border-indigo-200 dark:border-indigo-500/30',
  },
};

/** Colore del pallino di ogni cassetto, nello stile piatto (solo nel segno, mai nel testo). */
const COLORE_CATEGORIA: Record<CategoriaApprendimento, string> = {
  tone_of_voice: '#7C3AED',
  hook: '#D97706',
  errori: '#DC2626',
  preferenze: '#DB2777',
  regole: '#0284C7',
  cta: '#059669',
  contesto: '#4F46E5',
};

/** Quanto resta "nuovo" un apprendimento nella vista dedicata. */
const GIORNI_NOVITA = 14;

const eNuovo = (v: Voce): boolean =>
  Date.now() - new Date(v.created_at).getTime() < GIORNI_NOVITA * 24 * 60 * 60 * 1000;

const dataBreve = (iso: string): string =>
  new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * Pagina "Apprendimento": cosa questo AI Assistant ha imparato su QUESTO
 * cliente parlando col copywriter.
 *
 * Non è la cronologia della chat — quella è già nella barra laterale. Qui ci
 * sono solo le regole estratte da quelle conversazioni, divise per categoria:
 * ognuna rientra nel prompt del cliente a ogni generazione successiva, quindi
 * questa pagina risponde a una domanda sola: «cosa sa oggi di questo cliente
 * che sei mesi fa non sapeva?».
 */
const ApprendimentoAssistente: React.FC<Props> = ({
  clientId, clientName, getToken, segnaleNuovoScambio,
}) => {
  const [aperto, setAperto] = useState(false);
  const [memoria, setMemoria] = useState<Memoria | null>(null);
  const [inCaricamento, setInCaricamento] = useState(false);
  const [vista, setVista] = useState<'nuovi' | 'tutti' | CategoriaApprendimento>('nuovi');

  // correzione di una regola già imparata
  const [inModifica, setInModifica] = useState<string | null>(null);
  const [testoModifica, setTestoModifica] = useState('');
  const [categoriaModifica, setCategoriaModifica] = useState<CategoriaApprendimento>('regole');

  const authHeaders = useCallback(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }),
    [getToken]
  );

  // Il conteggio precedente serve solo ad accorgersi che è entrato qualcosa di
  // nuovo dopo l'ultimo messaggio, per dirlo a chi scrive.
  const totalePrecedente = useRef<number | null>(null);

  const carica = useCallback(
    async (silenzioso = false): Promise<Memoria | null> => {
      if (!silenzioso) setInCaricamento(true);
      try {
        const res = await fetch(`/api/ai/learnings?client_id=${clientId}`, { headers: authHeaders() });
        const data = await res.json();
        const m: Memoria | null = data.success ? data.data : null;
        setMemoria(m);
        return m;
      } catch {
        if (!silenzioso) toast.error('Non riesco a leggere gli apprendimenti');
        return null;
      } finally {
        if (!silenzioso) setInCaricamento(false);
      }
    },
    [clientId, authHeaders]
  );

  // Quante cose sa, già al primo sguardo: il numero sta sul pulsante. Il totale
  // di partenza si fissa qui, non al primo scambio: altrimenti la prima
  // correzione della sessione non verrebbe segnalata, che è proprio quella che
  // chi scrive si aspetta di veder registrata.
  useEffect(() => {
    totalePrecedente.current = null;
    carica(true).then((m) => {
      totalePrecedente.current = m?.totale ?? 0;
    });
  }, [clientId, carica]);

  /**
   * Dopo ogni risposta l'assistente rilegge lo scambio e ne ricava le regole:
   * gira sul server dopo aver risposto, quindi qui aspettiamo qualche secondo
   * prima di ricontrollare. Se è entrato qualcosa, lo diciamo: chi scrive deve
   * accorgersi che la correzione appena data è stata registrata.
   */
  useEffect(() => {
    if (!segnaleNuovoScambio) return;
    const t = setTimeout(async () => {
      const m = await carica(true);
      if (!m) return;
      const prima = totalePrecedente.current;
      totalePrecedente.current = m.totale;
      if (prima !== null && m.totale > prima) {
        const quanti = m.totale - prima;
        toast.success(
          quanti === 1 ? 'Ho imparato una cosa nuova su questo cliente' : `Ho imparato ${quanti} cose nuove su questo cliente`,
          { icon: '🎓' }
        );
      }
    }, 9000);
    return () => clearTimeout(t);
  }, [segnaleNuovoScambio, carica]);

  const apri = () => {
    setAperto(true);
    setVista('nuovi');
    setInModifica(null);
    carica();
  };

  const aggiorna = async (id: string, campi: Record<string, unknown>) => {
    try {
      const res = await fetch(`/api/ai/learnings/${id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(campi),
      });
      const data = await res.json();
      if (data.success) {
        await carica(true);
      } else {
        toast.error(data.errors?.[0]?.message || data.message || 'Modifica non riuscita');
      }
    } catch {
      toast.error('Errore di rete');
    }
  };

  const elimina = async (id: string) => {
    if (!window.confirm('L\'assistente dimenticherà questa regola. Procedo?')) return;
    try {
      const res = await fetch(`/api/ai/learnings/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        const m = await carica(true);
        totalePrecedente.current = m?.totale ?? null;
        toast.success('Dimenticata');
      } else {
        toast.error(data.message || 'Eliminazione fallita');
      }
    } catch {
      toast.error('Errore di rete');
    }
  };

  const tutte: Voce[] = memoria ? memoria.gruppi.flatMap((g) => g.voci) : [];

  /** Le voci della vista scelta: "nuovi" pesca da tutte le categorie. */
  const vociVista: Voce[] = (() => {
    if (vista === 'nuovi') {
      return tutte
        .filter(eNuovo)
        .sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
    }
    if (vista === 'tutti') {
      return [...tutte].sort((a, b) => +new Date(b.updated_at) - +new Date(a.updated_at));
    }
    return memoria?.gruppi.find((g) => g.categoria === vista)?.voci ?? [];
  })();

  const quantiNuovi = tutte.filter(eNuovo).length;

  return (
    <>
      {/* Il pulsante sta accanto a "Scopri come ragiono": lì c'è cosa sa di
          partenza, qui cosa ha imparato strada facendo. */}
      <button
        onClick={apri}
        title={`Cosa ha imparato l'assistente su ${clientName} dalle tue correzioni`}
        aria-label="Apprendimento dell'assistente"
        className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-500 dark:text-slate-400 hover:border-[#fdf751] hover:text-slate-800 dark:hover:text-slate-100 hover:bg-[#fdf751]/10 transition-colors"
      >
        <GraduationCap size={13} />
        Apprendimento
        {!!memoria?.attivi && (
          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#fdf751] text-slate-900 text-[10px] font-bold">
            {memoria.attivi}
          </span>
        )}
      </button>

      <AnimatedModal
        isOpen={aperto}
        onClose={() => setAperto(false)}
        title={`Cosa ho imparato su ${clientName}`}
        size="xl"
      >
        {inCaricamento && !memoria ? (
          <div className="py-10 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
            <p className="mt-2 text-sm text-slate-500">Rileggo la memoria di questo cliente…</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs sm:text-sm leading-relaxed" style={{ color: '#5B6270' }}>
              Imparo da solo, mentre lavoriamo. Ogni volta che correggi un gancio, il tono, una
              struttura o mi dai un'informazione nuova su {clientName}, me la annoto qui e me la
              porto dietro in tutto quello che scriverò dopo. Se ho capito male, correggi la regola, spegnila o cancellala.
            </p>

            {/* Tre numeri in fila, senza riquadri */}
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {[
                { etichetta: 'Regole che seguo', valore: memoria?.attivi ?? 0 },
                { etichetta: 'Spente da te', valore: (memoria?.totale ?? 0) - (memoria?.attivi ?? 0) },
                { etichetta: 'Imparate di recente', valore: quantiNuovi },
              ].map((v) => (
                <div key={v.etichetta}>
                  <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 1.1, textTransform: 'uppercase', color: '#8B92A0' }}>{v.etichetta}</div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums leading-none" style={{ color: '#13161C', letterSpacing: '-0.02em' }}>{v.valore}</div>
                </div>
              ))}
              <div className="self-end text-[11px]" style={{ color: '#8B92A0' }}>
                {memoria?.ultimoApprendimento
                  ? `Ultima cosa imparata il ${dataBreve(memoria.ultimoApprendimento)}`
                  : 'Non ho ancora imparato niente: continua a usarmi in chat e comincio.'}
              </div>
            </div>

            {/* I cassetti: testo con pallino, l'attivo è in nero e sottolineato */}
            <div className="flex flex-wrap gap-x-4 gap-y-2 pt-3" style={{ borderTop: '1px solid #EBEDF1' }}>
              {[
                { id: 'nuovi' as const, etichetta: 'Nuovi', n: quantiNuovi, colore: '#13161C' },
                { id: 'tutti' as const, etichetta: 'Tutto', n: tutte.length, colore: '#13161C' },
                ...(memoria?.gruppi ?? []).map((g) => ({
                  id: g.categoria,
                  etichetta: g.etichetta,
                  n: g.totale,
                  colore: COLORE_CATEGORIA[g.categoria],
                })),
              ].map((t) => {
                const attiva = vista === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => { setVista(t.id as any); setInModifica(null); }}
                    className="inline-flex items-center gap-1.5 text-sm pb-0.5 transition-colors"
                    style={{
                      color: attiva ? '#13161C' : '#5B6270',
                      fontWeight: attiva ? 600 : 400,
                      borderBottom: attiva ? '2px solid #13161C' : '2px solid transparent',
                    }}
                  >
                    {t.id !== 'nuovi' && t.id !== 'tutti' && (
                      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: t.colore }} />
                    )}
                    {t.etichetta}
                    <span className="tabular-nums" style={{ color: '#8B92A0' }}>{t.n}</span>
                  </button>
                );
              })}
            </div>

            {/* A cosa serve il cassetto aperto */}
            {vista !== 'nuovi' && vista !== 'tutti' && (
              <p className="text-xs" style={{ color: '#8B92A0' }}>{STILE_CATEGORIA[vista].descrizione}</p>
            )}
            {vista === 'nuovi' && (
              <p className="text-xs" style={{ color: '#8B92A0' }}>
                Quello che ho assimilato negli ultimi {GIORNI_NOVITA} giorni, dal più recente.
              </p>
            )}

            {/* Le regole: righe separate da un filetto, pallino del cassetto, niente sfondi */}
            <div className="max-h-[45vh] overflow-y-auto pr-1" style={{ borderTop: '1px solid #EBEDF1' }}>
              {vociVista.map((v) => {
                const colore = COLORE_CATEGORIA[v.categoria];
                const inCorrezione = inModifica === v.id;

                return (
                  <div
                    key={v.id}
                    className="py-3"
                    style={{ borderBottom: '1px solid #EBEDF1', opacity: v.attivo ? 1 : 0.5 }}
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-2 inline-block w-2 h-2 rounded-full" style={{ backgroundColor: colore }} />
                      <div className="min-w-0 flex-1">
                        {inCorrezione ? (
                          <div className="space-y-2">
                            <textarea
                              value={testoModifica}
                              onChange={(e) => setTestoModifica(e.target.value.slice(0, 220))}
                              rows={2}
                              className="w-full resize-none rounded-md border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:border-slate-400"
                            />
                            {/* Anche il cassetto può essere sbagliato: capita che
                                archivi sotto il tono di voce una regola di scrittura. */}
                            <select
                              value={categoriaModifica}
                              onChange={(e) => setCategoriaModifica(e.target.value as CategoriaApprendimento)}
                              className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:border-slate-400"
                            >
                              {(memoria?.gruppi ?? []).map((g) => (
                                <option key={g.categoria} value={g.categoria}>{g.etichetta}</option>
                              ))}
                            </select>
                            <div className="flex items-center gap-3">
                              <Button
                                size="sm"
                                variant="primary"
                                icon={Check}
                                disabled={testoModifica.trim().length < 10}
                                onClick={async () => {
                                  await aggiorna(v.id, {
                                    regola: testoModifica.trim(),
                                    categoria: categoriaModifica,
                                  });
                                  setInModifica(null);
                                }}
                              >
                                Salva
                              </Button>
                              <button onClick={() => setInModifica(null)} className="text-xs" style={{ color: '#5B6270' }}>
                                Annulla
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm leading-snug" style={{ color: '#13161C' }}>{v.regola}</p>
                        )}

                        {v.esempio && !inCorrezione && (
                          <p className="mt-1.5 flex items-start gap-1 text-[11px] leading-relaxed" style={{ color: '#5B6270' }}>
                            <Quote size={10} className="flex-shrink-0 mt-0.5" style={{ color: '#8B92A0' }} />
                            <span className="italic">{v.esempio}</span>
                          </p>
                        )}

                        {/* Da dove nasce: senza questo è una regola calata dall'alto */}
                        {v.origine && !inCorrezione && (
                          <p className="mt-1.5 text-[10px] leading-relaxed" style={{ color: '#8B92A0' }}>
                            Dalle tue parole: «{v.origine}»
                          </p>
                        )}

                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]" style={{ color: '#8B92A0' }}>
                          <span>{memoria?.gruppi.find((g) => g.categoria === v.categoria)?.etichetta}</span>
                          <span>Imparata il {dataBreve(v.created_at)}</span>
                          {v.conferme > 1 && <span>me l'hai ribadita {v.conferme} volte</span>}
                          {!v.attivo && <span>spenta: non la uso più</span>}
                        </div>
                      </div>

                      {!inCorrezione && (
                        <div className="flex flex-shrink-0 items-center gap-0.5">
                          <button
                            onClick={() => {
                              setInModifica(v.id);
                              setTestoModifica(v.regola);
                              setCategoriaModifica(v.categoria);
                            }}
                            title="Correggi la regola o spostala di categoria"
                            className="p-1.5 rounded-md transition-colors hover:bg-slate-100"
                            style={{ color: '#8B92A0' }}
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => aggiorna(v.id, { attivo: !v.attivo })}
                            title={v.attivo ? 'Smetti di usarla (resta qui)' : 'Rimettila in uso'}
                            className="p-1.5 rounded-md transition-colors hover:bg-slate-100"
                            style={{ color: '#8B92A0' }}
                          >
                            {v.attivo ? <Eye size={13} /> : <EyeOff size={13} />}
                          </button>
                          <button
                            onClick={() => elimina(v.id)}
                            title="Dimenticala del tutto"
                            className="p-1.5 rounded-md transition-colors hover:bg-slate-100 hover:text-red-600"
                            style={{ color: '#8B92A0' }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {vociVista.length === 0 && (
                <div className="py-8 text-center">
                  <p className="text-sm" style={{ color: '#5B6270' }}>
                    {tutte.length === 0
                      ? `Non ho ancora imparato niente su ${clientName}.`
                      : vista === 'nuovi'
                      ? `Niente di nuovo negli ultimi ${GIORNI_NOVITA} giorni.`
                      : 'Questo cassetto è ancora vuoto.'}
                  </p>
                  <p className="mt-1 text-[11px]" style={{ color: '#8B92A0' }}>
                    Correggimi in chat, un gancio, il tono, una struttura, e me lo annoto qui da solo.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </AnimatedModal>
    </>
  );
};

export default ApprendimentoAssistente;
